<template>
  <div class="glass-card p-6 rounded-xl border border-slate-700/50 relative overflow-hidden"
       style="background: linear-gradient(to top right, rgba(15, 23, 42, 0.9) 0%, rgba(30, 41, 59, 0.8) 50%, rgba(15, 23, 42, 0.9) 100%);">
    <div class="absolute top-0 right-0 w-32 h-32 bg-purple-500/10 rounded-full blur-3xl pointer-events-none"></div>
    <div class="relative z-10">
      <div class="flex items-center justify-between mb-4">
        <h2 class="text-xl font-bold text-white flex items-center gap-2">
          <i class="ph-bold ph-list-bullets text-blue-400"></i>
          操作日志
        </h2>
        <div class="flex gap-2">
          <div class="relative">
            <input
              v-model="searchUserId"
              type="text"
              placeholder="用户ID..."
              class="px-3 py-1.5 pr-8 bg-slate-800 border border-slate-600 rounded-lg text-sm text-white placeholder-slate-400 focus:outline-none focus:border-blue-500"
              @input="handleUserIdSearch"
            />
            <button
              v-if="searchUserId"
              @click="clearUserIdSearch"
              class="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
              title="清空"
            >
              <i class="ph-bold ph-x text-sm"></i>
            </button>
          </div>
          <div class="relative action-dropdown" ref="dropdownContainer">
            <button
              type="button"
              @click.stop="actionDropdownOpen = !actionDropdownOpen"
              class="min-w-[120px] pl-3 pr-8 py-1.5 bg-slate-800 border border-slate-600 rounded-lg text-sm text-white text-left focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/40 transition-all shadow-[0_0_0_1px_rgba(59,130,246,0.25)] flex items-center justify-between"
            >
              <span class="truncate">{{ actionLabel }}</span>
              <i class="ph-bold ph-caret-down text-slate-400 text-xs"></i>
            </button>
            <Teleport to="body">
              <Transition name="dropdown-fade">
                <div
                  v-if="actionDropdownOpen"
                  class="fixed bg-slate-900/95 border border-slate-700 rounded-xl shadow-2xl backdrop-blur p-1 z-[9999]"
                  :style="dropdownStyle"
                  @click.stop
                >
                  <button
                    v-for="opt in actionOptions"
                    :key="opt.value"
                    type="button"
                    @click="selectAction(opt.value)"
                    class="w-full text-left px-3 py-2 rounded-lg text-sm text-slate-200 hover:bg-blue-500/15 hover:text-white transition flex items-center justify-between"
                    :class="searchAction === opt.value ? 'bg-blue-500/10 border border-blue-500/40' : 'border border-transparent'"
                  >
                    <span>{{ opt.label }}</span>
                    <i v-if="searchAction === opt.value" class="ph-bold ph-check text-blue-400 text-xs"></i>
                  </button>
                </div>
              </Transition>
            </Teleport>
          </div>
        </div>
      </div>
      <div v-if="loading" class="text-center py-8 text-slate-400">
        加载中...
      </div>
      <div v-else class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b border-slate-700">
              <th class="text-left py-3 px-4 text-slate-300">ID</th>
              <th class="text-left py-3 px-4 text-slate-300">用户ID</th>
              <th class="text-left py-3 px-4 text-slate-300">操作类型</th>
              <th class="text-left py-3 px-4 text-slate-300">操作描述</th>
              <th class="text-left py-3 px-4 text-slate-300">操作时间</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="log in pagedLogs"
              :key="log.id"
              class="border-b border-slate-800 hover:bg-slate-800/50 transition"
            >
              <td class="py-3 px-4 text-white">{{ log.id }}</td>
              <td class="py-3 px-4 text-white">{{ log.userId || log.opUserId }}</td>
              <td class="py-3 px-4">
                <span class="px-2 py-1 rounded text-xs" :class="getActionClass(log.action)">
                  {{ getActionLabel(log.action) }}
                </span>
              </td>
              <td class="py-3 px-4 text-slate-300">{{ log.description }}</td>
              <td class="py-3 px-4 text-slate-400">{{ formatDate(log.createdAt) }}</td>
            </tr>
            <tr v-if="logs.length === 0">
              <td colspan="5" class="py-8 text-center text-slate-400">
                暂无操作日志
              </td>
            </tr>
          </tbody>
        </table>
        
        <!-- 分页 -->
        <div v-if="!loading && totalItems > 0" class="mt-4 pt-4 border-t border-slate-700 flex items-center justify-between">
          <div class="text-sm text-slate-400">
            共 <span class="text-white font-medium">{{ totalItems }}</span> 条记录，第 <span class="text-white font-medium">{{ page }}</span> / <span class="text-white font-medium">{{ totalPages }}</span> 页
          </div>
          <div class="flex items-center gap-2">
            <button
              @click="$emit('page-change', page - 1)"
              :disabled="page <= 1"
              class="px-3 py-1.5 text-sm rounded-lg border transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              :class="page <= 1 ? 'bg-slate-800/50 text-slate-600 border-slate-700' : 'bg-slate-800/50 text-slate-300 border-slate-600 hover:bg-slate-700/50 hover:border-blue-500/50'"
            >
              <i class="ph-bold ph-caret-left"></i>
            </button>
            <div class="flex items-center gap-1">
              <template v-for="p in visiblePages" :key="p">
                <button
                  v-if="p !== '...'"
                  @click="$emit('page-change', p)"
                  class="min-w-[32px] px-2 py-1.5 text-sm rounded-lg border transition-all"
                  :class="p === page 
                    ? 'bg-blue-500/20 text-blue-400 border-blue-500/50' 
                    : 'bg-slate-800/50 text-slate-300 border-slate-600 hover:bg-slate-700/50 hover:border-blue-500/50'"
                >
                  {{ p }}
                </button>
                <span v-else class="px-2 text-slate-500">...</span>
              </template>
            </div>
            <button
              @click="$emit('page-change', page + 1)"
              :disabled="page >= totalPages"
              class="px-3 py-1.5 text-sm rounded-lg border transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              :class="page >= totalPages ? 'bg-slate-800/50 text-slate-600 border-slate-700' : 'bg-slate-800/50 text-slate-300 border-slate-600 hover:bg-slate-700/50 hover:border-blue-500/50'"
            >
              <i class="ph-bold ph-caret-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, watch, onBeforeUnmount } from 'vue';

const props = defineProps({
  logs: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  },
  page: { type: Number, default: 1 },
  pageSize: { type: Number, default: 10 },
  total: { type: Number, default: 0 }
});

const emit = defineEmits(['load', 'page-change']);

const searchUserId = ref('');
const searchAction = ref('');
const actionDropdownOpen = ref(false);
const dropdownContainer = ref(null);
const dropdownStyle = ref({});

// 操作类型选项（根据实际数据库中的操作类型）
const actionOptions = [
  { value: '', label: '全部操作' },
  { value: 'list_users', label: '查询用户列表' },
  { value: 'toggle_user_status', label: '切换用户状态' },
  { value: 'update_user', label: '更新用户信息' },
  { value: 'delete_user', label: '删除用户' },
  { value: 'batch_delete_users', label: '批量删除用户' },
  { value: 'get_parent_account', label: '查询父账户' },
  { value: 'get_sub_accounts', label: '查询子账户' },
  { value: 'mark_file_resolved', label: '标记文件已解决' },
  { value: 'toggle_file_resolved', label: '切换文件状态' },
  { value: 'create_op', label: '创建运维管理员' }
];

// 获取当前操作类型标签
const actionLabel = computed(() => {
  const option = actionOptions.find(opt => opt.value === searchAction.value);
  return option ? option.label : '全部操作';
});

// 防抖定时器
let searchDebounceTimer = null;

// 处理用户ID搜索（添加防抖）
const handleUserIdSearch = () => {
  // 清除之前的定时器
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer);
  }
  // 设置新的定时器，500ms后触发搜索
  searchDebounceTimer = setTimeout(() => {
    // 传递用户ID字符串，空字符串会被父组件转换为null
    const userId = searchUserId.value.trim();
    const action = searchAction.value || '';
    emit('load', {
      userId: userId || '',
      action: action
    });
  }, 500);
};

// 清空用户ID搜索
const clearUserIdSearch = () => {
  searchUserId.value = '';
  const action = searchAction.value || '';
  emit('load', {
    userId: '',
    action: action
  });
};

// 选择操作类型
const selectAction = (value) => {
  searchAction.value = value;
  actionDropdownOpen.value = false;
  // 筛选时立即触发，不需要防抖
  const userId = searchUserId.value.trim();
  emit('load', {
    userId: userId || '',
    action: value || ''
  });
};

// 更新下拉菜单位置
const updateDropdownPosition = () => {
  if (dropdownContainer.value && actionDropdownOpen.value) {
    nextTick(() => {
      const rect = dropdownContainer.value.getBoundingClientRect();
      dropdownStyle.value = {
        top: `${rect.bottom + window.scrollY + 8}px`,
        left: `${rect.right + window.scrollX - 160}px`, // 160px 是下拉菜单宽度
        width: '160px'
      };
    });
  }
};

// 点击外部关闭下拉菜单
const handleClickOutside = (event) => {
  if (!actionDropdownOpen.value) return;
  
  // 检查点击是否在按钮容器内
  const isClickInContainer = dropdownContainer.value && dropdownContainer.value.contains(event.target);
  
  // 检查点击是否在下拉菜单内（通过查找固定定位的元素）
  const dropdownElements = document.querySelectorAll('.fixed.z-\\[9999\\]');
  let isClickInDropdown = false;
  dropdownElements.forEach(el => {
    if (el.contains(event.target)) {
      isClickInDropdown = true;
    }
  });
  
  // 如果点击既不在容器内也不在下拉菜单内，则关闭
  if (!isClickInContainer && !isClickInDropdown) {
    actionDropdownOpen.value = false;
  }
};

// 监听点击外部事件和窗口滚动
watch(actionDropdownOpen, (isOpen) => {
  if (isOpen) {
    nextTick(() => {
      updateDropdownPosition();
      document.addEventListener('click', handleClickOutside);
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
    });
  } else {
    document.removeEventListener('click', handleClickOutside);
    window.removeEventListener('scroll', updateDropdownPosition, true);
    window.removeEventListener('resize', updateDropdownPosition);
  }
});

// 组件卸载时移除事件监听和定时器
onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
  window.removeEventListener('scroll', updateDropdownPosition, true);
  window.removeEventListener('resize', updateDropdownPosition);
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer);
  }
});

const totalItems = computed(() => props.total || props.logs.length);

const totalPages = computed(() => {
  return Math.ceil(totalItems.value / props.pageSize) || 1;
});

// 后端已经做了分页，直接使用返回的数据，不再进行前端分页
const pagedLogs = computed(() => {
  // 如果后端返回的是分页数据，直接使用（不再切片）
  // 因为后端已经根据page和pageSize返回了对应页的数据
  return props.logs;
});

const visiblePages = computed(() => {
  const pages = [];
  const current = props.page;
  const total = totalPages.value;
  
  if (total <= 7) {
    for (let i = 1; i <= total; i++) {
      pages.push(i);
    }
  } else {
    if (current <= 4) {
      for (let i = 1; i <= 5; i++) {
        pages.push(i);
      }
      pages.push('...');
      pages.push(total);
    } else if (current >= total - 3) {
      pages.push(1);
      pages.push('...');
      for (let i = total - 4; i <= total; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      pages.push('...');
      for (let i = current - 1; i <= current + 1; i++) {
        pages.push(i);
      }
      pages.push('...');
      pages.push(total);
    }
  }
  
  return pages;
});

// 操作类型显示映射（将英文操作类型转换为中文）
const getActionLabel = (action) => {
  const actionMap = {
    'list_users': '查询用户列表',
    'toggle_user_status': '切换用户状态',
    'update_user': '更新用户信息',
    'delete_user': '删除用户',
    'batch_delete_users': '批量删除用户',
    'get_parent_account': '查询父账户',
    'get_sub_accounts': '查询子账户',
    'mark_file_resolved': '标记文件已解决',
    'toggle_file_resolved': '切换文件状态',
    'create_op': '创建运维管理员'
  };
  return actionMap[action] || action;
};

const getActionClass = (action) => {
  // 根据操作类型分类设置颜色
  if (action?.includes('delete') || action?.includes('remove')) {
    return 'bg-red-500/20 text-red-400';
  } else if (action?.includes('create') || action?.includes('add')) {
    return 'bg-green-500/20 text-green-400';
  } else if (action?.includes('update') || action?.includes('edit') || action?.includes('toggle')) {
    return 'bg-yellow-500/20 text-yellow-400';
  } else if (action?.includes('list') || action?.includes('get') || action?.includes('query')) {
    return 'bg-blue-500/20 text-blue-400';
  } else {
    return 'bg-slate-500/20 text-slate-400';
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleString('zh-CN');
};
</script>

<style scoped>
/* 下拉菜单过渡动画 */
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.dropdown-fade-enter-from {
  opacity: 0;
  transform: translateY(-8px) scale(0.95);
}

.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.95);
}
</style>

